import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFilteredActivities } from "@/entities/activities/hooks";
import { ActivityData } from "@/entities/activities/types";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import {
  useActivityTitle,
  useActivityType
} from "@/services/hooks/useActivity";
import { DreamerSource } from "@/entities/tasks/types";

const TRANSLATION_KEY = "tasks.drawer.task.dreamer";

// Branded so it can never be structurally confused with (or silently
// substituted by) `LinkedEntity` in CreatedByDreamerIndication.tsx — the two
// carry the same fields but mean different things (fetch-routing vs.
// navigation target), and that mix-up was a real bug.
export interface SourceEntity {
  entityType: EntityType;
  entityId: string;
  readonly __brand: "SourceEntity";
}

// The activity's own owning ids are what decide where it can actually be
// viewed — a task can be linked to a different record than its source
// activity (e.g. a task on a deal whose source email is on a lead), so
// navigation must follow the activity's ids, never the task's.
//
// Same priority as getActivityLookupEntity (CreatedByDreamerIndication.tsx):
// deal wins over lead, then company, then person. Kept as a separate
// function rather than a shared generic because the two read structurally
// different inputs (a flat prefixed id array vs. this typed record) and a
// generic keyed on EntityType would have to reintroduce the unsupported
// entity types (USER/CONTRACT/PARTNER/PARTNER_CONTACT) as a runtime branch
// instead of leaving them absent at compile time.
const getSourceEntity = (
  activity: ActivityData | undefined
): SourceEntity | undefined => {
  if (!activity) return undefined;
  if (activity.dealId) {
    return {
      entityType: EntityType.DEAL,
      entityId: activity.dealId,
      __brand: "SourceEntity"
    };
  }
  if (activity.leadId) {
    return {
      entityType: EntityType.LEAD,
      entityId: activity.leadId,
      __brand: "SourceEntity"
    };
  }
  if (activity.companyId) {
    return {
      entityType: EntityType.COMPANY,
      entityId: activity.companyId,
      __brand: "SourceEntity"
    };
  }
  const personId = activity.peopleIds?.[0];
  if (personId) {
    return {
      entityType: EntityType.PERSON,
      entityId: personId,
      __brand: "SourceEntity"
    };
  }
  return undefined;
};

// Called by `SourceRow`, which only mounts while the popover is open (Radix
// portals `PopoverContent` and doesn't render it while closed), so the
// activity is fetched on open — never once per task row on mount.
export const useSourceActivity = (
  source: DreamerSource,
  entityType?: EntityType,
  entityId?: string
) => {
  const { t } = useTranslation();

  // `useFilteredActivities` requires a parent entityName + entityId pair —
  // any entity type the task is linked to (deal, lead, company, person) with
  // an Activities tab works here. No linked entity means no drill-down link.
  const shouldFetch = Boolean(
    entityType !== undefined && entityId && source.activityId
  );
  const {
    entities: [activityData],
    isLoading
  } = useFilteredActivities(
    entityType !== undefined ? entityTypeToEntityName[entityType] : "",
    entityId || "",
    shouldFetch ? { id: { eq: source.activityId } } : {},
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    shouldFetch
  );

  const activityTitle = useActivityTitle(activityData);
  const activityType = useActivityType(t, activityData);
  const sourceEntity = useMemo(
    () => getSourceEntity(activityData),
    [activityData]
  );

  // Only known once the fetch has settled — resolving this before then would
  // mean the row renders once with a placeholder/raw value, then again with
  // the real one, which is the flicker this hook exists to avoid.
  const isReady = !shouldFetch || !isLoading;

  const displayTitle = isReady
    ? activityTitle ||
      source.activityTitle ||
      t(`${TRANSLATION_KEY}.fallbackSourceTitle`)
    : undefined;
  const displayType = isReady
    ? activityType || source.activityType || undefined
    : undefined;

  return { isReady, displayTitle, displayType, shouldFetch, sourceEntity };
};
